import { Spinner } from '@chakra-ui/react';
import { Box } from '@components/ui/atoms/Box';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { usePermissionCheck } from '@shared/permissions';
import React from 'react';

type PermissionGuardProps = {
  requiredPermissions?: string[];
  requireAll?: boolean;
  loadingFallback?: React.ReactNode;
  fallback?: React.ReactNode;
  children: React.ReactElement;
};

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  requiredPermissions = [],
  requireAll = false,
  loadingFallback,
  fallback,
  children,
}) => {
  const { hasAll, hasAny, isChecking } = usePermissionCheck();

  if (requiredPermissions.length === 0) {
    return children;
  }

  if (isChecking) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }

    return (
      <Box w="full" h="full" display="flex" alignItems="center" justifyContent="center" p="12">
        <Spinner />
      </Box>
    );
  }

  const isAllowed = requireAll ? hasAll(requiredPermissions) : hasAny(requiredPermissions);

  if (!isAllowed) {
    return (fallback as React.ReactElement) ?? <PermissionDenied />;
  }

  return children;
};

const PermissionDenied: React.FC = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="full"
    h="full"
    p="8"
    textAlign="center"
  >
    <Stack gap="3" maxW="480px">
      <Text fontSize="lg" fontWeight="600">
        You do not have permission to view this page.
      </Text>
      <Text color="body.dark" fontSize="md">
        If you think this is a mistake, please contact your administrator to request access.
      </Text>
    </Stack>
  </Box>
);
